import React, { useState, useEffect, useCallback } from "react";
import {
  uploadGalleryImage,
  deleteGalleryImage,
  getGalleryImages,
} from "../firebase";
import { resizeImage, validateImageFile } from "../utils/imageResizer";
import Toast from "../components/Toast";
import "./GalleryManagement.css";

const TrashIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="spinner-icon" viewBox="0 0 50 50">
    <circle
      className="path"
      cx="25"
      cy="25"
      r="20"
      fill="none"
      strokeWidth="5"
    ></circle>
  </svg>
);

interface GalleryImage {
  id: string;
  url: string;
  fileName: string;
  name: string;
  size: number;
  uploadedAt: string;
  width: number;
  height: number;
}

interface ToastAction {
  text: string;
  onClick: () => void;
}

interface ToastState {
  isVisible: boolean;
  message: string;
  type: "success" | "error" | "info";
  action?: ToastAction;
}

const GalleryManagement: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
    fileName: string;
  } | null>(null);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    message: "",
    type: "info",
    action: undefined,
  });

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "info",
    action?: ToastAction
  ) => {
    setToast({ isVisible: true, message, type, action });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const loadGalleryData = useCallback(async () => {
    try {
      setLoading(true);
      const imagesData = await getGalleryImages();
      const sortedImages = Object.values(imagesData).sort(
        (a, b) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
      setImages(sortedImages);
    } catch (error) {
      console.error("Error loading gallery data:", error);
      showToast("갤러리 데이터를 불러오는데 실패했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGalleryData();
  }, [loadGalleryData]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, fileName: "" });

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({
          current: i + 1,
          total: files.length,
          fileName: file.name,
        });

        const validation = validateImageFile(file);
        if (!validation.isValid) {
          showToast(validation.message || "파일 검증 실패.", "error");
          continue;
        }

        let processedFile = file;
        try {
          processedFile = await resizeImage(file, 0.2);
        } catch (error) {
          console.error("Image resize failed:", error);
          showToast(`${file.name} 처리 실패.`, "error");
          continue;
        }

        await uploadGalleryImage(processedFile);
      }

      await loadGalleryData();
      showToast(`${files.length}개의 이미지 업로드 완료.`, "success");
    } catch (error) {
      console.error("Error uploading images:", error);
      showToast("이미지 업로드 중 오류 발생.", "error");
    } finally {
      setUploading(false);
      setUploadProgress(null);
      event.target.value = "";
    }
  };

  const handleDeleteImage = async (imageToDelete: GalleryImage) => {
    if (deletingIds.has(imageToDelete.id)) return;

    if (
      !window.confirm(
        `'${imageToDelete.name}' 이미지를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      return;
    }

    setDeletingIds((prev) => new Set(prev).add(imageToDelete.id));

    try {
      await deleteGalleryImage(imageToDelete.id, imageToDelete.fileName);
      setImages((prev) => prev.filter((img) => img.id !== imageToDelete.id));
      showToast("이미지가 성공적으로 삭제되었습니다.", "success");
    } catch (error) {
      console.error("Error deleting image:", error);
      showToast("이미지 삭제에 실패했습니다.", "error");
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(imageToDelete.id);
        return newSet;
      });
    }
  };

  return (
    <div className="gallery-management">
      <h1 className="page-title">갤러리 관리</h1>
      <div className="upload-section card">
        <h2 className="card-title">이미지 업로드</h2>
        <p className="card-description">
          새로운 이미지를 갤러리에 추가합니다. 이미지는 자동으로 리사이징됩니다.
        </p>
        <div className="upload-controls">
          <input
            type="file"
            id="file-upload"
            multiple
            onChange={handleFileUpload}
            accept="image/png, image/jpeg, image/gif"
            disabled={uploading}
            className="file-input"
          />
          <label
            htmlFor="file-upload"
            className={`upload-button ${uploading ? "disabled" : ""}`}
          >
            {uploading ? "업로드 중..." : "파일 선택"}
          </label>
        </div>
        {uploading && uploadProgress && (
          <div className="upload-progress">
            <p>{`(${uploadProgress.current}/${uploadProgress.total}) ${uploadProgress.fileName} 업로드 중...`}</p>
            <div className="progress-bar-container">
              <div
                className="progress-bar"
                style={{
                  width: `${
                    (uploadProgress.current / uploadProgress.total) * 100
                  }%`,
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      <div className="image-list-section card">
        <h2 className="card-title">업로드된 이미지</h2>
        <p className="card-description">
          휴지통 아이콘을 클릭하여 이미지를 삭제할 수 있습니다.
        </p>
        {loading ? (
          <p>이미지를 불러오는 중...</p>
        ) : (
          <div className="image-grid">
            {images.map((image) => {
              const isDeleting = deletingIds.has(image.id);
              return (
                <div
                  key={image.id}
                  className={`image-item ${isDeleting ? "deleting" : ""}`}
                >
                  <img src={image.url} alt={image.name} className="thumbnail" />
                  <div className="image-overlay">
                    {isDeleting ? (
                      <SpinnerIcon />
                    ) : (
                      <button
                        onClick={() => handleDeleteImage(image)}
                        className="delete-button"
                        title="삭제"
                        disabled={isDeleting}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        action={toast.action}
      />
    </div>
  );
};

export default GalleryManagement;
